import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FileText, ArrowLeft, Settings, Image as ImageIcon, ChevronRight, Eye } from "lucide-react";
import { listInvoices, getBillingSettings, viewInvoicePdf } from "../../services/invoiceService";
import { getProject } from "../../services/projectService";
import { useAuthedImage } from "../../hooks/useAuthedImage";

const STATUS_META = {
  draft:   { bg: "#f1f5f9", color: "#475569", label: "Draft" },
  sent:    { bg: "#eff6ff", color: "#1d4ed8", label: "Sent" },
  partial: { bg: "#fff7ed", color: "#c2410c", label: "Partial" },
  paid:    { bg: "#f0fdf4", color: "#166534", label: "Paid" },
};

const InvoiceList = () => {
  const { id: projectPid } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [template, setTemplate] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingPid, setViewingPid] = useState(null);
  const logoSrc = useAuthedImage(template?.theme?.logoUrl);

  const handleViewPdf = async (inv) => {
    if (!inv.pdfUrl) return;
    setViewingPid(inv.invoicePid);
    try {
      await viewInvoicePdf(inv.pdfUrl);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to open PDF");
    } finally {
      setViewingPid(null);
    }
  };

  useEffect(() => {
    if (!projectPid) return;
    getProject(projectPid)
      .then((res) => setProject(res.data?.response ?? res.data))
      .catch(() => {});
    getBillingSettings(projectPid)
      .then((res) => setTemplate(res.data?.response ?? null))
      .catch(() => {});
    setLoading(true);
    listInvoices(projectPid, { status: "sent,partial,paid" })
      .then((res) => setInvoices(res.data?.response?.items ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectPid]);

  const accent = template?.theme?.accentColor || "#2563eb";

  return (
    <main className="app-wrapper url-list-page">
      <div className="container-fluid">
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
          <button
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
            style={{ borderRadius: 8 }}
            onClick={() => navigate(`/project-details/${projectPid}`)}
          >
            <ArrowLeft size={14} /> Project
          </button>
          <div>
            <h5 style={{ margin: 0, fontWeight: 800, color: "#0f172a" }}>Invoices</h5>
            <p style={{ margin: 0, fontSize: 12, color: "var(--muteColor)" }}>{project?.project_name ?? "—"}</p>
          </div>
        </div>

        {/* ── Billing template preview (edit-only) ── */}
        <div style={{ background: "#fff", border: "1px solid var(--borderColor)", borderRadius: 14, boxShadow: "0 1px 3px rgba(15,23,42,.05)", overflow: "hidden", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: "1px solid var(--borderColor)", background: "#fafbfc" }}>
            <div>
              <h6 style={{ margin: 0, fontWeight: 700, fontSize: 13, color: "#0f172a" }}>Billing Template</h6>
              <p style={{ margin: 0, fontSize: 11, color: "var(--muteColor)" }}>Used automatically whenever an invoice is generated for this project</p>
            </div>
            <button
              className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2"
              style={{ borderRadius: 8 }}
              onClick={() => navigate(`/project-details/${projectPid}/invoices/settings`)}
            >
              <Settings size={13} /> Edit Template
            </button>
          </div>
          <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
            <div style={{ width: 56, height: 56, borderRadius: 10, background: "#f8fafc", border: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
              {logoSrc
                ? <img src={logoSrc} alt="logo" style={{ maxWidth: "100%", maxHeight: "100%" }} />
                : <ImageIcon size={20} color="#94a3b8" />}
            </div>
            <div style={{ minWidth: 160 }}>
              <p style={{ margin: 0, fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>Business</p>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{template?.from?.name || "Not set"}</p>
            </div>
            <div style={{ minWidth: 100 }}>
              <p style={{ margin: 0, fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>Currency</p>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{template?.currency || "USD"}</p>
            </div>
            <div style={{ minWidth: 100 }}>
              <p style={{ margin: 0, fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>Taxes</p>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{template?.taxes?.length ?? 0} configured</p>
            </div>
            <div style={{ minWidth: 100 }}>
              <p style={{ margin: 0, fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>Discounts</p>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{template?.discounts?.length ?? 0} configured</p>
            </div>
            <div style={{ width: 10, height: 32, borderRadius: 4, background: accent, marginLeft: "auto" }} title="Accent color" />
          </div>
        </div>

        {/* ── Sent invoices ── */}
        <div style={{ background: "#fff", border: "1px solid var(--borderColor)", borderRadius: 14, boxShadow: "0 1px 3px rgba(15,23,42,.05), 0 4px 12px rgba(15,23,42,.03)", overflow: "hidden" }}>
          {loading ? (
            <div className="py-5 text-center text-muted">Loading...</div>
          ) : invoices.length === 0 ? (
            <div style={{ padding: "48px 20px", textAlign: "center" }}>
              <div style={{ width: 56, height: 56, borderRadius: 14, background: "#f1f5f9", margin: "0 auto 12px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <FileText size={24} color="#94a3b8" strokeWidth={1.5} />
              </div>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "#334155" }}>No invoices sent yet</p>
              <p style={{ margin: "4px 0 0", fontSize: 12, color: "#94a3b8" }}>Invoices sent to clients will show up here.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table url-table mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Invoice #</th>
                    <th>Client</th>
                    <th>Issue Date</th>
                    <th>Due Date</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv, i) => {
                    const st = STATUS_META[inv.status] || STATUS_META.draft;
                    return (
                      <tr key={inv.invoicePid}>
                        <td className="url-index">{i + 1}</td>
                        <td style={{ fontWeight: 700, fontSize: 13 }}>{inv.invoiceNumber}</td>
                        <td style={{ fontSize: 13 }}>{inv.to?.name || "—"}</td>
                        <td style={{ fontSize: 13 }}>{inv.issueDate || "—"}</td>
                        <td style={{ fontSize: 13 }}>{inv.dueDate || "—"}</td>
                        <td style={{ fontSize: 13, fontWeight: 700 }}>{inv.currencySymbol}{Number(inv.total || 0).toFixed(2)}</td>
                        <td>
                          <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: st.bg, color: st.color }}>{st.label}</span>
                        </td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              style={{ borderRadius: 7 }}
                              onClick={() => handleViewPdf(inv)}
                              disabled={!inv.pdfUrl || viewingPid === inv.invoicePid}
                              title={!inv.pdfUrl ? "PDF not generated yet" : "View PDF"}
                            >
                              <Eye size={13} />
                            </button>
                            <button
                              className="ph-view-btn"
                              onClick={() => navigate(`/project-details/${projectPid}/invoices/${inv.invoicePid}/view`)}
                            >
                              View <ChevronRight size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default InvoiceList;
